import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import PhoneMockup from "@/components/PhoneMockup";
import EventGalleryScreen from "@/components/mockups/EventGalleryScreen";
import UploadScreen from "@/components/mockups/UploadScreen";

// Balinese temple / Indonesian wedding decor (face-less)
const HERO_IMG =
  "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?crop=entropy&cs=srgb&fm=jpg&w=940&q=85";

export default function Hero() {
  const { t } = useLang();

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative overflow-hidden bg-white pt-32 lg:pt-40"
    >
      {/* subtle grain */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.03] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 pb-24 lg:grid-cols-12 lg:gap-8 lg:px-8 lg:pb-32">
        {/* Left */}
        <div className="lg:col-span-6 lg:pt-6">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="hero-chip"
            className="inline-flex items-center gap-2 rounded-full border border-neutral-300 bg-white px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-neutral-700"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            {t.hero.chip}
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            data-testid="hero-headline"
            className="mt-6 font-['Outfit'] text-[44px] font-light leading-[0.98] tracking-tight text-neutral-950 sm:text-6xl lg:text-[80px]"
          >
            EVERY GUEST <br />
            <span className="italic font-['Playfair_Display']">has a</span> STORY.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-6 max-w-md font-['DM_Sans'] text-base leading-relaxed text-neutral-600 lg:text-lg"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-10 flex items-center gap-4"
          >
            <a
              href="#reach"
              data-testid="hero-cta-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-neutral-950 px-6 py-3.5 text-sm text-white transition-all hover:bg-neutral-800"
            >
              {t.hero.cta}
              <ArrowUpRight
                className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                strokeWidth={1.75}
              />
            </a>

            <div className="hidden items-center gap-3 sm:flex">
              <div className="flex -space-x-2">
                {["#C9A878", "#DDE7E4", "#1A1A1A"].map((c, i) => (
                  <span
                    key={i}
                    className="grid h-8 w-8 place-items-center rounded-full border-2 border-white text-[10px] font-medium"
                    style={{
                      background: c,
                      color: c === "#1A1A1A" ? "#C9A878" : "#1A1A1A",
                    }}
                  >
                    {["ID", "★", "M"][i]}
                  </span>
                ))}
              </div>
              <p className="max-w-[160px] text-[11px] leading-tight text-neutral-500">
                Trusted by 200+ events across Indonesia
              </p>
            </div>
          </motion.div>

          {/* meta row */}
          <div className="mt-16 grid max-w-md grid-cols-3 gap-4 border-t border-neutral-200 pt-6">
            <MetaCell k="20k+" v="Memories" />
            <MetaCell k="200+" v="Events" />
            <MetaCell k="98%" v="Retention" />
          </div>
        </div>

        {/* Right — phones */}
        <div className="relative lg:col-span-6">
          <div
            className="absolute -right-24 -top-10 hidden h-[420px] w-[420px] rounded-full lg:block"
            style={{
              background:
                "radial-gradient(circle at 30% 30%, rgba(221,231,228,0.9), rgba(197,212,206,0) 65%)",
            }}
          />

          <div className="relative flex items-center justify-center gap-6 lg:justify-end">
            <PhoneMockup
              data-testid="hero-phone-1"
              className="relative z-10 -translate-y-4"
              rotate={-4}
            >
              <UploadScreen />
            </PhoneMockup>
            <PhoneMockup
              data-testid="hero-phone-2"
              className="relative -translate-y-16 lg:-translate-y-24"
              rotate={5}
              delay={0.15}
            >
              <EventGalleryScreen
                title="Sarah & David"
                date="12.06.2026"
                photos={342}
                videos={58}
                contributors={87}
                image={HERO_IMG}
              />
            </PhoneMockup>
          </div>
        </div>
      </div>
    </section>
  );
}

function MetaCell({ k, v }) {
  return (
    <div>
      <p className="font-['Playfair_Display'] text-2xl text-neutral-950">{k}</p>
      <p className="mt-1 text-[10px] uppercase tracking-[0.15em] text-neutral-500">{v}</p>
    </div>
  );
}
